import styled from "@emotion/styled";

import {
    reactUiModalBottomPadding,
    reactUiModalHeaderBottomPadding,
    reactUiModalHeaderRightPadding,
    reactUiModalLeftPadding,
    reactUiModalRightPadding,
} from "../../../../Internals/ThemeProvider/ReactUiModalPaddings";
import { sizeToken } from "../../../../Internals/ThemeProvider/CandyTheme/ThemeBuilder/ICandyTheme";
import { Length } from "../../../../Internals/ThemeProvider/CandyTheme/Utils/CssLength";
import { theme } from "../../../../Internals/ThemeProvider/CandyTheme/ThemeShorthand";

export const Header = styled.h3(props => ({
    padding: 0,
    margin: 0,
    ...props.theme.candy.modals.headerFont.css(),
}));

export const HeaderPanel = styled.div(props => ({
    fontSize: props.theme.candy.typography.primary.fontSize.css(),
    width: 800 - reactUiModalLeftPadding - reactUiModalRightPadding,
    paddingRight: reactUiModalRightPadding,
    marginRight: -reactUiModalHeaderRightPadding,
    marginBottom: -reactUiModalHeaderBottomPadding,
}));

export const HeaderPanelLine = styled.div<{ paddingLeft?: number; marginLeft?: number }>(props => ({
    marginTop: sizeToken(1),
    paddingLeft: props.paddingLeft,
    marginLeft: props.marginLeft,
}));

export const HorizontalLoaderContainer = styled.div({
    marginLeft: -reactUiModalLeftPadding,
    marginRight: -reactUiModalRightPadding,
});

export const HeaderTable = styled.table(_ => ({
    width: 800 - reactUiModalLeftPadding - reactUiModalRightPadding,
    marginTop: sizeToken(1),
    tableLayout: "fixed",
    marginBottom: 0,
    borderSpacing: 0,
}));

export const HeaderBottomBorder = styled.div(props => ({
    height: 1,
    marginLeft: -reactUiModalLeftPadding,
    marginRight: -reactUiModalRightPadding,
    backgroundColor: props.theme.candy.tables.border.color,
}));

export const HeaderTableCell = styled.th(props => ({
    textAlign: "left",
    ...props.theme.candy.tables.headers.smallFont.css(),
}));

export const ItemTableContainer = styled.div<{ overlay: boolean }>(
    {
        minHeight: 300,
        position: "relative",
        marginLeft: -reactUiModalLeftPadding,
        marginRight: -reactUiModalRightPadding,
        marginBottom: -reactUiModalBottomPadding,
    },
    props => ({ ...theme(props).typography.primary.css() }),
    props =>
        props.overlay && {
            "::before": {
                content: '""',
                position: "absolute",
                backgroundColor: props.theme.candy.loadingOverlayBackgroundColor,
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
            },
        }
);

export const ItemTable = styled.div({
    width: 800,
    borderSpacing: 0,
    tableLayout: "fixed",
});

export const RestItemsMessage = styled.div(props => ({
    paddingTop: sizeToken(2),
    paddingBottom: sizeToken(2),
    marginLeft: reactUiModalLeftPadding,
    marginRight: reactUiModalRightPadding,
    ...props.theme.candy.typography.smallFont.css(),
}));

export const ItemTableLeftPaddingCell = styled.div({
    width: reactUiModalLeftPadding,
    paddingTop: sizeToken(1),
    paddingBottom: sizeToken(1),
    verticalAlign: "top",
    display: "inline-block",
});

export const ItemTableCell = styled.div<{ width?: number | string }>(props => ({
    paddingTop: sizeToken(1),
    paddingBottom: sizeToken(1),
    verticalAlign: "top",
    display: "inline-block",
    width: props.width,
}));

export const ItemTableActionsCell = styled.div({
    paddingTop: sizeToken(1),
    paddingBottom: sizeToken(1),
    verticalAlign: "top",
    overflow: "visible",
    display: "inline-block",
});

export const RemovedPanel = styled.div<{ active: boolean }>(props => ({
    visibility: props.active ? "visible" : "hidden",
    opacity: props.active ? 1 : 0,
    transition: `opacity 0.15s 0s ease-in`,
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 800,
    textAlign: "center",
    backgroundColor: props.theme.candy.semanticColors.warning.backgroundColor,
    boxSizing: "border-box",
    paddingTop: sizeToken(1),
}));

export const RemovePanelContent = styled.div(_ => ({
    position: "absolute",
    left: "50%",
    display: "inline-block",
    top: "50%",
    transform: "translateY(-50%) translateX(-50%)",
}));

export const ActionsContainer = styled.div({
    position: "absolute",
    right: 0,
    top: 0,
    width: 100,
    height: sizeToken(4.5),
    textAlign: "right",
    // paddingRight Подобран так, чтобы крестик на элементе и на модалке были выровнены
    paddingTop: Length.halfToken().css(),
    boxSizing: "border-box",
    paddingRight: reactUiModalRightPadding - 8,
});

export const RoundedButton = styled.div(
    props => ({
        cursor: "pointer",
        border: 0,
        outline: 0,
        width: sizeToken(3.5),
        height: sizeToken(3.5),
        borderRadius: sizeToken(3.5) / 2,
        display: "inline-block",
        textAlign: "center",
        padding: "3px 0 0 0",
        boxSizing: "border-box",
        lineHeight: Length.tokens(2.5).css(),
        color: props.theme.candy.semanticColors.action.color,

        ":hover": {
            backgroundColor: props.theme.candy.graphics.light.strokeColor,
        },
    }),
    {
        marginLeft: Length.token().css(),
    }
);

export const EmptyMessageContainer1 = styled.div(props => ({
    ...theme(props).typography.primary.css(),
    height: 300,
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
}));

export const EmptyMessageContainer2 = styled.div({
    textAlign: "center",
});

export const ItemTableRow = styled.div<{ removed: boolean }>(props => ({
    cursor: props.removed ? undefined : "pointer",
    backgroundColor: props.theme.candy.lists.items.default.backgroundColor,
    position: "relative",
    lineHeight: "20px",

    [`${ActionsContainer}`]: {
        display: "none",
    },

    ":hover": {
        backgroundColor: props.theme.candy.lists.items.accentLow.backgroundColor,

        [`${ActionsContainer}`]: {
            display: "block",
            backgroundColor: props.theme.candy.lists.items.accentLow.backgroundColor,
            background: `linear-gradient(90deg, transparent 0%, ${props.theme.candy.lists.items.accentLow.backgroundColor} 20%, ${props.theme.candy.lists.items.accentLow.backgroundColor} 100%)`,
        },
    },
}));

export const EmptyMessageHeader = styled.div(props => ({
    ...props.theme.candy.typography.header4.css(),
}));
